/**
 * MedPass Validation Errors Component
 * 
 * Displays validation errors and warnings for the MedPass workflow,
 * helping users understand what needs to be completed before approval.
 */

import React from 'react';
import {
    Alert,
    List,
    Card,
    Row,
    Col,
    Space,
    Tag,
    Typography,
    Empty,
    Steps,
    Divider
} from 'antd';
import {
    ExclamationCircleOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const MedPassValidationErrors = ({ validation }) => {
    if (!validation) {
        return (
            <Empty
                description="No validation data available"
            />
        );
    }

    const { is_valid, errors = [], warnings = [], next_step, can_proceed } = validation;

    // Error type configurations
    const errorTypeConfig = {
        REQUIRED_FIELD: {
            icon: <ExclamationCircleOutlined />,
            color: 'red',
            title: 'Required Field Missing'
        },
        SAFETY_CHECK_REQUIRED: {
            icon: <WarningOutlined />,
            color: 'orange',
            title: 'Safety Check Required'
        },
        HIGH_RISK_MEDICATION: {
            icon: <ExclamationCircleOutlined />,
            color: 'red',
            title: 'High-Risk Medication'
        },
        INVALID_DATA: {
            icon: <CloseCircleOutlined />,
            color: 'red',
            title: 'Invalid Data'
        },
        BUSINESS_RULE: {
            icon: <InfoCircleOutlined />,
            color: 'blue',
            title: 'Business Rule Violation'
        }
    };

    // Step mapping for display
    const stepMapping = {
        'patient_demographics': 'Patient Demographics',
        'medication_order_entry': 'Medication Order Entry',
        'pre_post_conditions': 'Pre/Post Conditions',
        'physician_notes_scheduling': 'Physician Notes & Scheduling'
    };

    const renderValidationSummary = () => (
        <Alert
            message="Validation Summary"
            description={
                <Row gutter={16}>
                    <Col>
                        <Space>
                            <Text>Status:</Text>
                            <Tag color={is_valid ? 'green' : 'red'}>
                                {is_valid ? 'Valid' : 'Invalid'}
                            </Tag>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Text>Errors:</Text>
                            <Tag color={errors.length > 0 ? 'red' : 'green'}>
                                {errors.length}
                            </Tag>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Text>Warnings:</Text>
                            <Tag color={warnings.length > 0 ? 'orange' : 'green'}>
                                {warnings.length}
                            </Tag>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Text>Can Proceed:</Text>
                            <Tag color={can_proceed ? 'green' : 'red'}>
                                {can_proceed ? 'Yes' : 'No'}
                            </Tag>
                        </Space>
                    </Col>
                </Row>
            }
            type={is_valid ? 'success' : 'error'}
            showIcon
            style={{ marginBottom: 16 }}
        />
    );

    const renderErrorCard = (error, index) => {
        const config = errorTypeConfig[error.code] || errorTypeConfig.INVALID_DATA;
        
        return (
            <Card
                key={index}
                size="small"
                style={{ marginBottom: 8 }}
                bodyStyle={{ padding: '12px 16px' }}
            >
                <Row align="middle" gutter={12}>
                    <Col flex="none">
                        <span style={{ color: config.color, fontSize: '16px' }}>
                            {config.icon}
                        </span>
                    </Col>
                    <Col flex="auto">
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Text strong>{config.title}</Text>
                                </Col>
                                <Col>
                                    <Tag color={config.color} size="small">
                                        {error.code}
                                    </Tag>
                                </Col>
                            </Row>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Field: <Text code>{error.field}</Text>
                            </Text>
                            <Paragraph style={{ margin: 0, fontSize: '14px' }}>
                                {error.message}
                            </Paragraph>
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderWarningCard = (warning, index) => (
        <Card
            key={index}
            size="small"
            style={{ marginBottom: 8, borderColor: '#faad14' }}
            bodyStyle={{ padding: '12px 16px' }}
        >
            <Row align="middle" gutter={12}>
                <Col flex="none">
                    <WarningOutlined style={{ color: '#faad14', fontSize: '16px' }} />
                </Col>
                <Col flex="auto">
                    <Text style={{ fontSize: '14px' }}>{warning}</Text>
                </Col>
            </Row>
        </Card>
    );

    const renderNextStepGuidance = () => {
        if (!next_step) return null;

        return (
            <Alert
                message="Next Step"
                description={
                    <div>
                        <Text>
                            Complete the <Text strong>{stepMapping[next_step]}</Text> step to continue.
                        </Text>
                        {!can_proceed && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="danger">
                                    All validation errors must be resolved before proceeding.
                                </Text>
                            </div>
                        )}
                    </div>
                }
                type="info"
                showIcon
                style={{ marginTop: 16 }}
            />
        );
    };

    const renderValidationChecklist = () => {
        // Define validation checklist items
        const checklistItems = [
            {
                key: 'patient_profile',
                title: 'Patient Profile Complete',
                completed: !errors.some(e => e.field === 'patient_profile')
            },
            {
                key: 'medications',
                title: 'Medications Added',
                completed: !errors.some(e => e.field === 'medications')
            },
            {
                key: 'safety_checks',
                title: 'Safety Checks Completed',
                completed: !errors.some(e => e.code === 'SAFETY_CHECK_REQUIRED')
            },
            {
                key: 'pre_conditions',
                title: 'Pre-conditions Defined',
                completed: !errors.some(e => e.field === 'pre_conditions')
            },
            {
                key: 'physician_notes',
                title: 'Physician Notes Added',
                completed: !errors.some(e => e.field === 'physician_notes')
            },
            {
                key: 'scheduling',
                title: 'Scheduling Information',
                completed: !errors.some(e => e.field === 'scheduling_info')
            }
        ];

        return (
            <Card 
                title="Validation Checklist" 
                size="small" 
                style={{ marginTop: 16 }}
            >
                <List
                    dataSource={checklistItems}
                    renderItem={(item) => (
                        <List.Item>
                            <Space>
                                {item.completed ? (
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                ) : (
                                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                )}
                                <Text 
                                    style={{ 
                                        textDecoration: item.completed ? 'line-through' : 'none',
                                        color: item.completed ? '#52c41a' : 'inherit'
                                    }}
                                >
                                    {item.title}
                                </Text>
                            </Space>
                        </List.Item>
                    )}
                />
            </Card>
        );
    };

    if (is_valid && errors.length === 0 && warnings.length === 0) {
        return (
            <div className="medpass-validation-success">
                <Alert
                    message="Validation Successful"
                    description="All validation checks have passed. The workflow is ready for approval."
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                />
                {renderValidationChecklist()}
            </div>
        );
    }

    return (
        <div className="medpass-validation-errors">
            {renderValidationSummary()}

            {/* Validation Errors */}
            {errors.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <Title level={5} style={{ color: '#ff4d4f', marginBottom: 12 }}>
                        <ExclamationCircleOutlined /> Validation Errors ({errors.length})
                    </Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        These errors must be resolved before the workflow can be approved.
                    </Text>
                    {errors.map((error, index) => renderErrorCard(error, index))}
                </div>
            )}

            {/* Validation Warnings */}
            {warnings.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <Title level={5} style={{ color: '#faad14', marginBottom: 12 }}>
                        <WarningOutlined /> Validation Warnings ({warnings.length})
                    </Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        These warnings should be reviewed but don't prevent approval.
                    </Text>
                    {warnings.map((warning, index) => renderWarningCard(warning, index))}
                </div>
            )}

            {renderNextStepGuidance()}
            {renderValidationChecklist()}

            {/* Help Information */}
            <Divider />
            <Alert
                message="Need Help?"
                description={
                    <div>
                        <Paragraph style={{ margin: 0, fontSize: '12px' }}>
                            • Review each error message carefully and complete the required fields or actions.
                        </Paragraph>
                        <Paragraph style={{ margin: 0, fontSize: '12px' }}>
                            • Run safety checks after adding medications to ensure patient safety.
                        </Paragraph>
                        <Paragraph style={{ margin: 0, fontSize: '12px' }}>
                            • Contact your system administrator if you encounter technical issues.
                        </Paragraph>
                    </div>
                }
                type="info"
                showIcon={false}
                style={{ marginTop: 16 }}
            />
        </div>
    );
};

export default MedPassValidationErrors;